import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, Volume2, VolumeX, SkipBack, SkipForward, Clock, User, Headphones, Youtube, Music, Trash2, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const categories = ["Të gjitha", "Adhurime", "Ahlak", "Sire", "Familje", "Akide"];

const AudioLectures = ({ isOpen, onClose, isAdmin, token }) => {
  const [lectures, setLectures] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("Të gjitha");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    speaker: 'Hoxhë Ahmed Kalaja',
    category: 'Adhurime',
    audio_url: '',
    video_url: '',
    duration: '10:00',
    type: 'audio'
  });
  const [currentLecture, setCurrentLecture] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      fetchLectures();
    }
  }, [isOpen]);

  const fetchLectures = async () => {
    try {
      const res = await axios.get(`${API}/lectures`);
      setLectures(res.data || []);
    } catch (error) {
      console.error('Error fetching lectures:', error);
      toast.error('Gabim në ngarkimin e ligjëratave');
      setLectures([]);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.speaker) {
      toast.error('Ju lutem plotësoni fushat kryesore');
      return;
    }

    setUploading(true);
    try {
      await axios.post(`${API}/admin/lectures`, formData, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('Ligjërata u shtua me sukses');
      setUploadOpen(false);
      setFormData({ title: '', speaker: 'Hoxhë Ahmed Kalaja', category: 'Adhurime', audio_url: '', video_url: '', duration: '10:00', type: 'audio' });
      fetchLectures();
    } catch (error) {
      console.error('Error uploading lecture:', error);
      toast.error('Gabim gjatë ruajtjes');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Dëshironi ta fshini këtë ligjëratë?')) return;
    try {
      await axios.delete(`${API}/admin/lectures/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      toast.success('U fshi me sukses');
      fetchLectures();
    } catch (error) {
      toast.error('Gabim gjatë fshirjes');
    }
  };

  const filteredLectures = selectedCategory === "Të gjitha"
    ? lectures
    : lectures.filter(l => l.category === selectedCategory);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentLecture]);

  const playLecture = (lecture) => {
    if (lecture.video_url || lecture.type === 'video') {
      setCurrentLecture(lecture);
      setShowVideo(true);
    } else {
      if (currentLecture?.id === lecture.id) {
        togglePlayPause();
      } else {
        setCurrentLecture(lecture);
        setIsPlaying(true);
        setCurrentTime(0);
        setShowVideo(false);
        setTimeout(() => {
          audioRef.current?.play();
        }, 100);
      }
    }
  };

  const togglePlayPause = () => {
    if (isPlaying) {
      audioRef.current?.pause();
    } else {
      audioRef.current?.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const skipTime = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(duration, currentTime + seconds));
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getYoutubeEmbedUrl = (url) => {
    if (!url) return null;
    // Handle various YouTube URL formats
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 
      ? `https://www.youtube.com/embed/${match[2]}?autoplay=1`
      : url;
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-md z-50 flex items-end sm:items-center justify-center"
        onClick={onClose}
        data-testid="audio-lectures-overlay"
      >
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="bg-white w-full sm:max-w-2xl sm:mx-4 max-h-[95vh] sm:max-h-[90vh] rounded-t-3xl sm:rounded-3xl overflow-hidden shadow-2xl flex flex-col"
          onClick={(e) => e.stopPropagation()}
          data-testid="audio-lectures-modal"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-violet-600 to-purple-700 p-4 sm:p-5 text-white flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <Headphones className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div>
                  <h2 className="text-lg sm:text-xl font-heading font-bold">Ligjërata</h2>
                  <p className="text-white/70 text-xs sm:text-sm">Audio & Video</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {isAdmin && (
                  <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
                    <DialogTrigger asChild>
                      <button className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-all">
                        <Plus className="w-5 h-5" />
                      </button>
                    </DialogTrigger>
                    <DialogContent className="max-w-md">
                      <DialogHeader>
                        <DialogTitle>Shto Ligjëratë</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleUpload} className="space-y-4 pt-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Titulli</Label>
                            <Input value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} placeholder="Titulli..." />
                          </div>
                          <div className="space-y-2">
                            <Label>Lektori</Label>
                            <Input value={formData.speaker} onChange={e => setFormData({...formData, speaker: e.target.value})} />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label>Kategoria</Label>
                            <Select value={formData.category} onValueChange={val => setFormData({...formData, category: val})}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {categories.filter(c => c !== "Të gjitha").map(c => (
                                  <SelectItem key={c} value={c}>{c}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label>Kohëzgjatja (p.sh 45:00)</Label>
                            <Input value={formData.duration} onChange={e => setFormData({...formData, duration: e.target.value})} />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label>Lloji</Label>
                          <Select value={formData.type} onValueChange={val => setFormData({...formData, type: val})}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="audio">Vetëm Audio</SelectItem>
                              <SelectItem value="video">Video (YouTube)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {formData.type === 'audio' ? (
                          <div className="space-y-2">
                            <Label>URL e Audio (mp3)</Label>
                            <Input value={formData.audio_url} onChange={e => setFormData({...formData, audio_url: e.target.value})} placeholder="https://..." />
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <Label>URL e YouTube</Label>
                            <Input value={formData.video_url} onChange={e => setFormData({...formData, video_url: e.target.value})} placeholder="https://youtube.com/..." />
                          </div>
                        )}
                        <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700" disabled={uploading}>
                          {uploading ? 'Duke ruajtur...' : 'Ruaj Ligjëratën'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                )}
                <button
                  onClick={onClose}
                  className="w-9 h-9 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center"
                  data-testid="audio-close-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Categories - Scrollable */}
            <div className="flex gap-2 mt-4 overflow-x-auto pb-1 scrollbar-hide">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium transition-all ${
                    selectedCategory === category
                      ? 'bg-white text-violet-600'
                      : 'bg-white/20 text-white'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Video Player (if showing video) */}
          <AnimatePresence>
            {showVideo && currentLecture && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-black flex-shrink-0"
              >
                <div className="relative pt-[56.25%]">
                  <iframe
                    className="absolute inset-0 w-full h-full"
                    src={getYoutubeEmbedUrl(currentLecture.video_url)}
                    title={currentLecture.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <div className="p-3 bg-gray-900 text-white">
                  <h3 className="font-semibold text-sm">{currentLecture.title}</h3>
                  <p className="text-xs text-gray-400">{currentLecture.speaker}</p>
                  <button
                    onClick={() => setShowVideo(false)}
                    className="mt-2 text-xs text-violet-400 hover:text-violet-300"
                  >
                    ← Kthehu tek lista
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Lectures List */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4">
            <div className="space-y-2">
              {filteredLectures.map((lecture, index) => (
                <motion.button
                  key={lecture.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => playLecture(lecture)}
                  className={`w-full text-left p-3 rounded-xl border-2 transition-all ${
                    currentLecture?.id === lecture.id
                      ? 'border-violet-500 bg-violet-50'
                      : 'border-gray-100 bg-white active:scale-[0.98]'
                  }`}
                  data-testid={`lecture-${lecture.id}`}
                >
                  <div className="flex gap-3">
                    {/* Cover */}
                    <div className={`w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br ${lecture.cover_color || 'from-violet-500 to-purple-600'} flex items-center justify-center flex-shrink-0 relative`}>
                      {currentLecture?.id === lecture.id && isPlaying ? (
                        <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 1 }}>
                          <Pause className="w-6 h-6 text-white" />
                        </motion.div>
                      ) : (
                        <Play className="w-6 h-6 text-white ml-0.5" />
                      )}
                      {/* Video/Audio Badge */}
                      {lecture.video_url || lecture.type === 'video' ? (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                          <Youtube className="w-3 h-3 text-white" />
                        </div>
                      ) : (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-violet-500 rounded-full flex items-center justify-center">
                          <Music className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base truncate">
                        {lecture.title}
                      </h3>
                      <div className="flex items-center gap-1 text-xs sm:text-sm text-gray-500 mt-0.5">
                        <User className="w-3 h-3" />
                        <span className="truncate">{lecture.speaker}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {lecture.duration}
                        </span>
                        <span className="bg-gray-100 px-2 py-0.5 rounded-full">
                          {lecture.category}
                        </span>
                      </div>
                    </div>

                    {isAdmin && (
                      <button 
                        onClick={(e) => handleDelete(e, lecture.id)}
                        className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Audio Player - Only show for audio content */}
          {currentLecture && !showVideo && !currentLecture.video_url && (
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className={`bg-gradient-to-r ${currentLecture.cover_color || 'from-violet-500 to-purple-600'} p-3 sm:p-4 text-white flex-shrink-0`}
            >
              <audio
                ref={audioRef}
                src={currentLecture.audio_url}
                preload="metadata"
              />

              {/* Now Playing */}
              <div className="flex items-center gap-3 mb-3">
                <div className="flex-1 min-w-0">
                  <h4 className="font-semibold text-sm truncate">{currentLecture.title}</h4>
                  <p className="text-xs text-white/70 truncate">{currentLecture.speaker}</p>
                </div>
              </div>

              {/* Progress Bar */}
              <div 
                className="h-1.5 bg-white/20 rounded-full cursor-pointer mb-2"
                onClick={handleSeek}
              >
                <div
                  className="h-full bg-white rounded-full transition-all"
                  style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
                />
              </div>

              {/* Time */}
              <div className="flex justify-between text-xs text-white/70 mb-3">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>

              {/* Controls */}
              <div className="flex items-center justify-center gap-4">
                <button
                  onClick={() => skipTime(-10)}
                  className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center active:scale-90"
                >
                  <SkipBack className="w-4 h-4" />
                </button>

                <button
                  onClick={togglePlayPause}
                  className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-violet-600 active:scale-90"
                  data-testid="audio-play-pause"
                >
                  {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
                </button>

                <button
                  onClick={() => skipTime(10)}
                  className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center active:scale-90"
                >
                  <SkipForward className="w-4 h-4" />
                </button>

                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center active:scale-90"
                >
                  {isMuted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                </button>
              </div>
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default AudioLectures;
